import { useEffect, useState } from "react";
import { Plus, X, Trash2, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import SelectField from "../../components/os/SelectField";
import { EmployeePicker } from "../../components/os/EmployeePicker";

const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet text-sm";
const CATS = [{ value: "técnica", label: "Técnica" }, { value: "blanda", label: "Blanda" }, { value: "liderazgo", label: "Liderazgo" }, { value: "idioma", label: "Idioma" }, { value: "digital", label: "Digital" }];

function Stars({ level, onSet }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} onClick={() => onSet && onSet(n)} className={onSet ? "cursor-pointer" : "cursor-default"}>
          <Star className={`w-4 h-4 ${n <= level ? "fill-violet text-violet" : "text-navy/20"}`} />
        </button>
      ))}
    </div>
  );
}

export default function Skills({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ employee: "", skill: "", category: "técnica", level: 3 });

  const load = () => api.get("/px/skills").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.employee.trim() || !f.skill.trim()) return toast.error("Colaborador y competencia son obligatorios");
    try { await api.post("/px/skills", f); setOpen(false); setF({ employee: "", skill: "", category: "técnica", level: 3 }); load(); toast.success("Competencia agregada"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const setLevel = async (s, level) => { try { await api.patch(`/px/skills/${s.id}`, { level }); setItems((prev) => prev.map((x) => x.id === s.id ? { ...x, level } : x)); } catch { /* */ } };
  const del = async (s) => { try { await api.delete(`/px/skills/${s.id}`); load(); } catch { /* */ } };

  const Shell = ({ children }) => embedded ? <>{children}</> : <OSLayout title="OMNIA Skills Management">{children}</OSLayout>;

  return (
    <Shell>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="skills-page">
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="overline text-navy/40 mb-2">OMNIA People Suite · Talent Management</div>
            {!embedded && <h2 className="font-editorial text-4xl tracking-tight">Competencias</h2>}
            <p className="mt-2 text-navy/50">Matriz de competencias por colaborador con nivel de dominio (1–5).</p>
          </div>
          <button onClick={() => setOpen(true)} className="btn btn-primary shrink-0" data-testid="skills-new"><Plus className="w-4 h-4" /> Agregar</button>
        </div>

        {loading ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> :
          items.length === 0 ? <div className="card-surface py-12 text-center text-navy/40 text-sm">Sin competencias registradas.</div> :
          <div className="card-surface overflow-x-auto" data-testid="skills-table">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-navy/10 text-navy/50"><th className="text-left font-medium px-4 py-3">Colaborador</th><th className="text-left font-medium px-4 py-3">Competencia</th><th className="text-left font-medium px-4 py-3">Categoría</th><th className="text-left font-medium px-4 py-3">Nivel</th><th /></tr></thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-b border-navy/5 last:border-0" data-testid={`skill-${s.id}`}>
                    <td className="px-4 py-3 font-medium">{s.employee}</td>
                    <td className="px-4 py-3">{s.skill}</td>
                    <td className="px-4 py-3"><span className="badge badge-violet">{CATS.find((c) => c.value === s.category)?.label || s.category}</span></td>
                    <td className="px-4 py-3"><Stars level={s.level} onSet={(n) => setLevel(s, n)} /></td>
                    <td className="px-4 py-3 text-right"><button onClick={() => del(s)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="skills-dialog">
          <div className="card-surface w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-2xl">Agregar competencia</h3><button onClick={() => setOpen(false)}><X className="w-5 h-5 text-navy/40" /></button></div>
            <div className="space-y-3">
              <EmployeePicker testid="skills-employee" value={null} placeholder="Colaborador"
                onChange={(emp) => setF({ ...f, employee: emp ? emp.name : "" })} />
              {f.employee && <div className="text-xs text-navy/50">Seleccionado: <b className="text-navy">{f.employee}</b></div>}
              <input className={input} placeholder="Competencia (ej. Excel avanzado)" value={f.skill} onChange={(e) => setF({ ...f, skill: e.target.value })} data-testid="skills-skill" />
              <SelectField value={f.category} options={CATS} onChange={(v) => setF({ ...f, category: v })} />
              <div><label className="text-xs text-navy/50 mb-1 block">Nivel de dominio</label><Stars level={f.level} onSet={(n) => setF({ ...f, level: n })} /></div>
            </div>
            <div className="flex justify-end gap-2 mt-5"><button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button><button onClick={create} className="btn btn-primary" data-testid="skills-submit">Agregar</button></div>
          </div>
        </div>
      )}
    </Shell>
  );
}
